import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '@/config/support';
import { usePageMeta } from '@/hooks/usePageMeta';

const Privacy = () => {
  usePageMeta({
    title: 'Privacy policy, CultureLens',
    description:
      'How CultureLens and IV Talent collect, use, and safeguard information from the CultureLens platform.',
    canonical: 'https://culturelens.org/privacy',
  });
  return (
    <div className="min-h-screen bg-background font-grotesk">
      <div className="container mx-auto px-6 py-16 max-w-3xl">
        <Button variant="ghost" asChild className="mb-8">
          <Link to="/" className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </Button>

        <h1 className="font-display text-3xl md:text-4xl tracking-tight mb-8">Privacy policy</h1>

        <p className="font-display text-xl md:text-2xl text-foreground leading-snug mb-10 pb-8 border-b">
          Nobody sees who said what. Nothing resolves to fewer than five people.
        </p>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-muted-foreground">
          <section>
            <h2 className="font-display text-xl text-foreground mb-3">Introduction</h2>
            <p>
              IV Talent ("we," "our," or "us") is committed to protecting your privacy. This Privacy 
              Policy explains how we collect, use, disclose, and safeguard your information when you 
              use our CultureLens platform and services.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">Information we collect</h2>
            <p>
              We may collect information that you provide directly to us, including but not limited to:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Contact information (name, email address, phone number)</li>
              <li>Account credentials</li>
              <li>Survey responses and feedback</li>
              <li>Communication preferences</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">How we use your information</h2>
            <p>
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Provide, maintain, and improve our services</li>
              <li>Process and complete transactions</li>
              <li>Send you technical notices and support messages</li>
              <li>Respond to your comments and questions</li>
              <li>Analyze usage patterns and trends</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">Data security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect the security 
              of your personal information. However, please note that no method of transmission over 
              the Internet or electronic storage is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">Data retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes 
              for which it was collected, including to satisfy any legal, accounting, or reporting 
              requirements.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">Your rights</h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal information, 
              including the right to access, correct, or delete your data. To exercise these rights, 
              please contact us at <a href={SUPPORT_MAILTO} className="underline">{SUPPORT_EMAIL}</a>.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">Contact us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us at:<br />
              Email: <a href={SUPPORT_MAILTO} className="underline">{SUPPORT_EMAIL}</a>
            </p>
          </section>
        </div>

        <p className="text-sm text-muted-foreground mt-12">
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </p>
      </div>
    </div>
  );
};

export default Privacy;
